import { useEffect, useState, FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import { data as dataApi } from '../../lib/api'
import Loading from '../../components/loading'
import { EntryReport } from '../../lib/types'
import { ActionReport } from '../../lib/types'
import '../../components/table/table.css'
import Template from '../../components/template'
import { Link } from 'react-router-dom'
import ErrorMsg from '../../components/error-msg'
import Alert from '../../components/alert'
import { WorkflowConfig } from '../../lib/types'
import { getWorkflowConfig } from '../util'
import { adjustEndDate, applyDateProcessingMethods, timezones } from '../../lib/date-util'
import Progress from '../../components/progress'

export default function ReportingPage() {
    const [loading, setLoading] = useState(false)
    const [entries, setEntries] = useState<EntryReport[]>()
    const [actions, setActions] = useState<ActionReport[]>()
    const [percentComplete, setPercentComplete] = useState<number | null>(null)
    const [config, setConfig] = useState<WorkflowConfig | null>(null)
    const [error, setError] = useState<any>()
    const [start, setStart] = useState('')
    const [end, setEnd] = useState('')
    const [timezone, setTimezone] = useState(timezones[0].value)
    const [today, setToday] = useState('')

    const { id } = useParams()

    useEffect(() => {
        getWorkflowConfig(Number(id))
            .then(setConfig)
            .catch((err) => setError(err.message || err))
    }, [id])

    useEffect(() => {
        const current = new Date().toISOString().substring(0, 10)
        setToday(current)
        setEnd(current)
        setStart(current)
    }, [])

    async function getReportData(e: FormEvent) {
        e.preventDefault()
        setError(false)
        setLoading(true)
        const adjustedEnd = adjustEndDate(end)
        const startString = applyDateProcessingMethods(start, timezone)
        const endString = applyDateProcessingMethods(adjustedEnd, timezone)
        dataApi
            .getReport(Number(id), startString, endString)
            .then((apiResults) => {
                setActions(apiResults.actionReport)
                setEntries(apiResults.entries)
                if (apiResults.percentComplete) {
                    setPercentComplete(apiResults.percentComplete)
                }
                setLoading(false)
            })
            .catch((err) => {
                setError(err.message || err)
                setLoading(false)
            })
    }

    return (
        <Template>
            <div className='mx-20 my-10'>
                <div className='mb-3'>
                    <Link to={'/workflows/' + id}>
                        <i className='lni lni-chevron-left' /> Back to Workflow
                    </Link>
                </div>
                {error && <ErrorMsg error={error} />}
                <h1 className='mb-5'>Reporting for {config?.name}</h1>
                <div className='mb-10'>
                    <form onSubmit={getReportData}>
                        <label htmlFor='start'>Start Date: </label>
                        <input
                            className='mb-5'
                            name='start'
                            type='date'
                            defaultValue={today}
                            onChange={(e) => {
                                setStart(e.target.value)
                            }}
                        />
                        <br />
                        <label htmlFor='end'>End Date: </label>
                        <input
                            name='end'
                            type='date'
                            defaultValue={today}
                            onChange={(e) => {
                                setEnd(e.target.value)
                            }}
                        />
                        <br />
                        <label htmlFor='timezone_offset'>Timezone: </label>
                        <select
                            className='mt-5'
                            name='timezone_offset'
                            onChange={(e) => {
                                setTimezone(Number(e.target.value))
                            }}
                        >
                            {timezones.map(({ name, value }) => (
                                <option value={value}>{name}</option>
                            ))}
                        </select>
                        <br />
                        <button className='blue my-5' disabled={loading}>View Report</button>
                    </form>
                </div>
                {loading && <Loading />}
                {!loading && error && <div>Unable to load table</div>}
                {!loading && entries && (
                    <div>
                        {percentComplete && (
                            <div>
                                <h2>Total Progress:</h2>
                                <Progress percentComplete={percentComplete} />
                            </div>
                        )}

                        <h1>Contributor Performance Summary</h1>
                        {entries.length < 1 && <Alert>No data for this time period</Alert>}
                        {entries.length > 0 && (
                            <table>
                                <thead>
                                    <tr>
                                        <th style={{textAlign: 'left', minWidth: '120px'}}>User</th>
                                        <th style={{textAlign: 'center', minWidth: '100px'}}>Total Entries</th>
                                        <th style={{textAlign: 'center', minWidth: '120px'}}>Not Reviewed</th>
                                        <th style={{textAlign: 'center', minWidth: '120px'}}>Reviewed/Approved</th>
                                        <th style={{textAlign: 'center', minWidth: '120px'}}>Total Reviewed</th>
                                        <th style={{textAlign: 'center', minWidth: '100px'}}>% Reviewed</th>
                                        <th style={{textAlign: 'center', minWidth: '100px'}}>Total Errors</th>
                                        <th style={{textAlign: 'center', minWidth: '130px'}}>Outstanding Errors</th>
                                        <th style={{textAlign: 'center', minWidth: '100px'}}>Error Rate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map(
                                        ({
                                            user,
                                            pendingEntries,
                                            errors,
                                            outstandingErrors,
                                            totalEntries,
                                            numberReviewed,
                                            totalReviewed,
                                            percentReviewed,
                                            errorRate,
                                        }) => (
                                            <tr key={user} style={{borderBottom: '1px solid #ddd'}}>
                                                <td style={{textAlign: 'left', fontWeight: user === 'Totals' ? 'bold' : 'normal', paddingRight: '20px'}}>{user}</td>
                                                <td style={{textAlign: 'center', fontWeight: user === 'Totals' ? 'bold' : 'normal'}}>{totalEntries}</td>
                                                <td style={{textAlign: 'center', fontWeight: user === 'Totals' ? 'bold' : 'normal'}}>{pendingEntries}</td>
                                                <td style={{textAlign: 'center', fontWeight: user === 'Totals' ? 'bold' : 'normal'}}>{numberReviewed}</td>
                                                <td style={{textAlign: 'center', fontWeight: user === 'Totals' ? 'bold' : 'normal'}}>{totalReviewed}</td>
                                                <td style={{textAlign: 'center', fontWeight: user === 'Totals' ? 'bold' : 'normal', color: percentReviewed < 0.5 && user !== 'Totals' ? '#e74c3c' : 'inherit'}}>
                                                    {(percentReviewed * 100).toFixed(1)}%
                                                </td>
                                                <td style={{textAlign: 'center', fontWeight: user === 'Totals' ? 'bold' : 'normal'}}>{errors}</td>
                                                <td style={{textAlign: 'center', fontWeight: user === 'Totals' ? 'bold' : 'normal', color: outstandingErrors > 0 && user !== 'Totals' ? '#e74c3c' : 'inherit'}}>
                                                    {outstandingErrors}
                                                </td>
                                                <td style={{textAlign: 'center', fontWeight: user === 'Totals' ? 'bold' : 'normal', color: errorRate > 0.1 && user !== 'Totals' ? '#e74c3c' : 'inherit'}}>
                                                    {(errorRate * 100).toFixed(1)}%
                                                </td>
                                            </tr>
                                        ),
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
                {!loading && actions && (
                    <div>
                        <h1>Reviewer Summary</h1>
                        {actions.length < 1 && <Alert>No data for this time period</Alert>}
                        {actions.length > 0 && (() => {
                            const isArbitrationWorkflow = config?.steps?.[0]?.isArbitrate
                            
                            return (
                                <table>
                                    <thead>
                                        <tr>
                                            <th style={{textAlign: 'left', minWidth: '120px'}}>User</th>
                                            <th style={{textAlign: 'center', minWidth: '100px'}}>Reviews</th>
                                            {isArbitrationWorkflow ? (
                                                <th style={{textAlign: 'center', minWidth: '130px'}}>Arbitration Merged</th>
                                            ) : (
                                                <>
                                                    <th style={{textAlign: 'center', minWidth: '100px'}}>Approvals</th>
                                                </>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {actions.map(({ user, review, error, duplicate, approve, arbitrationMerged, arbitrate }) => {
                                            const totalReviews = review + error + duplicate + arbitrate
                                            
                                            return (
                                                <tr key={user} style={{borderBottom: '1px solid #ddd'}}>
                                                    <td style={{textAlign: 'left', paddingRight: '20px'}}>{user}</td>
                                                    <td style={{textAlign: 'center'}}>{totalReviews}</td>
                                                    {isArbitrationWorkflow ? (
                                                        <td style={{textAlign: 'center'}}>{arbitrationMerged || 0}</td>
                                                    ) : (
                                                        <td style={{textAlign: 'center'}}>{approve}</td>
                                                    )}
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            )
                        })()}
                    </div>
                )}
            </div>
        </Template>
    )
}
